/**
 * useRealtimeAdminMetrics.js
 * Real-time Supabase subscriptions for Admin Dashboard live updates.
 * No polling - pure subscription-based updates.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const MAX_RETRIES = 8;
const BASE_RETRY_DELAY = 1500;

export function useRealtimeAdminMetrics({ enabled = true } = {}) {
  const [botAlerts, setBotAlerts] = useState([]);
  const [simMetrics, setSimMetrics] = useState(null);
  const [cheatAlerts, setCheatAlerts] = useState([]);
  const [channelStatuses, setChannelStatuses] = useState({
    botDetection: 'idle',
    simMetrics: 'idle',
    cheatDetection: 'idle',
  });
  const [newAlertIds, setNewAlertIds] = useState(new Set());

  const channelsRef = useRef({});
  const retryCountsRef = useRef({ botDetection: 0, simMetrics: 0, cheatDetection: 0 });
  const retryTimersRef = useRef({});
  const mountedRef = useRef(true);

  const setChannelStatus = useCallback((channel, status) => {
    if (!mountedRef?.current) return;
    setChannelStatuses(prev => ({ ...prev, [channel]: status }));
  }, []);

  const cleanupChannel = useCallback((name) => {
    if (retryTimersRef?.current?.[name]) {
      clearTimeout(retryTimersRef?.current?.[name]);
      delete retryTimersRef?.current?.[name];
    }
    if (channelsRef?.current?.[name]) {
      supabase?.removeChannel(channelsRef?.current?.[name]);
      delete channelsRef?.current?.[name];
    }
  }, []);

  const scheduleRetry = useCallback((channelName, subscribeFn) => {
    const retryCount = retryCountsRef?.current?.[channelName] || 0;
    if (retryCount >= MAX_RETRIES) {
      setChannelStatus(channelName, 'closed');
      return;
    }
    retryCountsRef.current[channelName] = retryCount + 1;
    const delay = BASE_RETRY_DELAY * Math.pow(1.5, retryCount);
    retryTimersRef.current[channelName] = setTimeout(() => {
      if (mountedRef?.current) subscribeFn();
    }, delay);
  }, [setChannelStatus]);

  const subscribeBotDetection = useCallback(() => {
    if (!mountedRef?.current || !enabled) return;
    cleanupChannel('botDetection');
    setChannelStatus('botDetection', 'connecting');

    const channel = supabase
      ?.channel('admin-bot-detection-log')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'bot_detection_log',
      }, (payload) => {
        if (!mountedRef?.current) return;
        const newEntry = payload?.new;
        if (!newEntry) return;
        setBotAlerts(prev => [newEntry, ...prev]?.slice(0, 50));
        setNewAlertIds(prev => {
          const next = new Set(prev);
          next?.add(newEntry?.id || ('bot-' + Date.now()));
          return next;
        });
      })
      ?.subscribe((status, err) => {
        if (!mountedRef?.current) return;
        if (status === 'SUBSCRIBED') {
          setChannelStatus('botDetection', 'connected');
          retryCountsRef.current.botDetection = 0;
        } else if (['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED']?.includes(status)) {
          setChannelStatus('botDetection', 'error');
          console.warn('[RealtimeAdmin] bot_detection_log channel error:', status, err);
          scheduleRetry('botDetection', subscribeBotDetection);
        }
      });

    channelsRef.current.botDetection = channel;
  }, [enabled, cleanupChannel, setChannelStatus, scheduleRetry]);

  const subscribeSimMetrics = useCallback(() => {
    if (!mountedRef?.current || !enabled) return;
    cleanupChannel('simMetrics');
    setChannelStatus('simMetrics', 'connecting');

    const channel = supabase
      ?.channel('admin-simulation-metrics')
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'simulation_metrics',
      }, (payload) => {
        if (!mountedRef?.current) return;
        const updated = payload?.new;
        if (!updated) return;
        setSimMetrics(updated);
      })
      ?.subscribe((status, err) => {
        if (!mountedRef?.current) return;
        if (status === 'SUBSCRIBED') {
          setChannelStatus('simMetrics', 'connected');
          retryCountsRef.current.simMetrics = 0;
        } else if (['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED']?.includes(status)) {
          setChannelStatus('simMetrics', 'error');
          console.warn('[RealtimeAdmin] simulation_metrics channel error:', status, err);
          scheduleRetry('simMetrics', subscribeSimMetrics);
        }
      });

    channelsRef.current.simMetrics = channel;
  }, [enabled, cleanupChannel, setChannelStatus, scheduleRetry]);

  const subscribeCheatDetection = useCallback(() => {
    if (!mountedRef?.current || !enabled) return;
    cleanupChannel('cheatDetection');
    setChannelStatus('cheatDetection', 'connecting');

    const channel = supabase
      ?.channel('admin-cheat-detection')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'suspicious_activity',
      }, (payload) => {
        if (!mountedRef?.current) return;
        const newAlert = payload?.new;
        if (!newAlert) return;
        setCheatAlerts(prev => [newAlert, ...prev]?.slice(0, 50));
        setNewAlertIds(prev => {
          const next = new Set(prev);
          next?.add(newAlert?.id || ('cheat-' + Date.now()));
          return next;
        });
      })
      ?.subscribe((status, err) => {
        if (!mountedRef?.current) return;
        if (status === 'SUBSCRIBED') {
          setChannelStatus('cheatDetection', 'connected');
          retryCountsRef.current.cheatDetection = 0;
        } else if (['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED']?.includes(status)) {
          setChannelStatus('cheatDetection', 'error');
          console.warn('[RealtimeAdmin] suspicious_activity channel error:', status, err);
          scheduleRetry('cheatDetection', subscribeCheatDetection);
        }
      });

    channelsRef.current.cheatDetection = channel;
  }, [enabled, cleanupChannel, setChannelStatus, scheduleRetry]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) {
      subscribeBotDetection();
      subscribeSimMetrics();
      subscribeCheatDetection();
    }
    return () => {
      mountedRef.current = false;
      cleanupChannel('botDetection');
      cleanupChannel('simMetrics');
      cleanupChannel('cheatDetection');
    };
  }, [enabled]);

  const clearNewAlert = useCallback((id) => {
    setNewAlertIds(prev => {
      const next = new Set(prev);
      next?.delete(id);
      return next;
    });
  }, []);

  const allConnected =
    channelStatuses?.botDetection === 'connected' &&
    channelStatuses?.simMetrics === 'connected' &&
    channelStatuses?.cheatDetection === 'connected';

  const hasError =
    channelStatuses?.botDetection === 'error' ||
    channelStatuses?.simMetrics === 'error' ||
    channelStatuses?.cheatDetection === 'error';

  const overallStatus = allConnected
    ? 'connected'
    : hasError
    ? 'error'
    : Object.values(channelStatuses)?.some(s => s === 'connecting')
    ? 'connecting' :'idle';

  return {
    botAlerts,
    simMetrics,
    cheatAlerts,
    channelStatuses,
    connectionStatus: overallStatus,
    allConnected,
    newAlertIds,
    clearNewAlert,
  };
}

export default useRealtimeAdminMetrics;
